<template>
    <template v-if="shouldShowMainScreen">
        <Teleport to=".view.services > .head">
            <div class="table-allocation-button-container" v-if="identity.hasOperatorAccess">
                <TableAllocationInfoButton
                    :showAllocationTime="false"
                    :showLoadingLabel="false"
                    @onCheckoutSelected="onCheckoutSelected"
                    @onRenameSelected="onRenameSelected"
                    @onPrintSelected="onPrintSelected"
                    @onAdjustEndUsersCountSelected="onAdjustEndUsersCountSelected"
                />
            </div>
        </Teleport>
    </template>

    <template v-if="shouldShowOffer">
        <Teleport to=".view.order > .head">
            <div class="table-allocation-button-container" v-if="identity.hasOperatorAccess">
                <TableAllocationInfoButton
                    :showAllocationTime="false"
                    :showLoadingLabel="false"
                    @onCheckoutSelected="onCheckoutSelected"
                    @onRenameSelected="onRenameSelected"
                    @onPrintSelected="onPrintSelected"
                    @onAdjustEndUsersCountSelected="onAdjustEndUsersCountSelected"
                />
            </div>
        </Teleport>
    </template>

    <template v-if="shouldShowPreorder">
        <Teleport to=".view.preorder-time > .head">
            <div class="table-allocation-button-container" v-if="identity.hasOperatorAccess">
                <TableAllocationInfoButton
                    :showAllocationTime="false"
                    :showLoadingLabel="false"
                    @onCheckoutSelected="onCheckoutSelected"
                    @onRenameSelected="onRenameSelected"
                    @onPrintSelected="onPrintSelected"
                    @onAdjustEndUsersCountSelected="onAdjustEndUsersCountSelected"
                />
            </div>
        </Teleport>
    </template>

    <template v-if="shouldShowPay">
        <Teleport to=".view.bill > .head">
            <div class="table-allocation-button-container" v-if="identity.hasOperatorAccess">
                <TableAllocationInfoButton
                    :showAllocationTime="false"
                    :showLoadingLabel="false"
                    @onCheckoutSelected="onCheckoutSelected"
                    @onRenameSelected="onRenameSelected"
                    @onPrintSelected="onPrintSelected"
                    @onAdjustEndUsersCountSelected="onAdjustEndUsersCountSelected"
                />
            </div>
        </Teleport>
    </template>
    <template v-if="shouldShowOpenBills">
        <Teleport to=".view.open-bills > .head">
            <div class="table-allocation-button-container" v-if="identity.hasOperatorAccess">
                <TableAllocationInfoButton
                    :showAllocationTime="false"
                    :showLoadingLabel="false"
                    @onCheckoutSelected="onCheckoutSelected"
                    @onRenameSelected="onRenameSelected"
                    @onPrintSelected="onPrintSelected"
                    @onAdjustEndUsersCountSelected="onAdjustEndUsersCountSelected"
                />
            </div>
        </Teleport>
    </template>
</template>

<script setup lang="ts">
    // this components works as a wrapper for the TableNameLabel component
    // it is used to teleport the TableNameLabel component to the correct place in the DOM
    // this is a solution to use Vue Components in vanilla TS files
    import { appEvents } from "@/app/events/appEvents";
    import { RenameBillPopover } from "@/app/popover/RenameBillPopover";
    import { Popup } from "@/app/ui/Popup";
    import { Factory } from "@/data/dao/Factory";
    import { OperatorDao } from "@/data/dao/service/OperatorDao";
    import { useBillStore } from "@/data/stores/bill";
    import { useIdentityStore } from "@/data/stores/identity";
    import { usePrintStore } from "@/data/stores/print";
    import { useTableAllocationStore } from "@/data/stores/tableAllocation";
    import { Format } from "@/data/util/Format";
    import { replaceString } from "@/data/util/String";
    import { clientLanguage } from "@/shared/localization/language";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { nextTick, ref } from "vue";

    import TableAllocationInfoButton from "@/components/tableAllocation/TableAllocationInfoButton.vue";

    const bill = useBillStore();
    const identity = useIdentityStore();

    const shouldShowMainScreen = ref(false);
    const shouldShowOffer = ref(false);
    const shouldShowPay = ref(false);
    const shouldShowOpenBills = ref(false);
    const shouldShowPreorder = ref(false);

    appEvents.controller.shown.add(async () => {
        await nextTick();
        shouldShowMainScreen.value = $(".view.services.shown").length > 0;
        shouldShowOffer.value = $(".view.order.shown").length > 0;
        shouldShowPay.value = $(".view.bill.shown").length > 0;
        shouldShowOpenBills.value = $(".view.open-bills.shown").length > 0;
        shouldShowPreorder.value = $(".view.preorder-time.shown").length > 0;
    });

    // const renameBillModalComponent = ref<InstanceType<typeof RenameBillModal> | null>(null);

    function onRenameSelected() {
        new RenameBillPopover().show();
        // renameBillModalComponent.value?.show();
    }

    async function onPrintSelected() {
        const print = usePrintStore();
        const bill = useBillStore();
        const allocation = useTableAllocationStore();

        if (bill.currentBill == null) {
            return;
        }

        try {
            await print.printInterimBill(bill.currentBill!.id, allocation!.locationId!, clientLanguage());
            Popup.show(translations.print.orderReceiptPrinted);
        } catch (ex) {
            logError(ex);
            Popup.show(translations.print.failedPrintReceipt);
        }
    }

    function onCheckoutSelected() {
        const allocation = useTableAllocationStore();
        const table = allocation.fullTableInfo;

        if (table == null || table.allocations == 0) return;

        Popup.showWithOptions({
            message: replaceString(
                translations.changeAllocationDetailsModal.resetTableText,
                table.allocations + "",
                Format.htmlEscape(table.name)!
            ),
            confirmHandler: async () => {
                try {
                    await allocation.removeGuests();
                } catch (ex) {
                    logError(ex);
                    Popup.showWithOptions({
                        message: translations.changeAllocationDetailsModal.endUsersCouldNotBeRemoved,
                    });
                }
            },
            confirmButton: translations.changeAllocationDetailsModal.resetTable,
            cancelButton: translations.general.cancel,
        });
    }

    async function onAdjustEndUsersCountSelected(count: number) {
        try {
            const operatorDao = Factory.getInstance<OperatorDao>(OperatorDao);
            await operatorDao.updateBillManualEndUsersCount(bill.currentBill!.id, {
                manualEndUsersCount: count,
            });
        } catch (ex) {
            logError(ex);
            Popup.showWithOptions({
                message: translations.tableAllocationContextMenu.adjustEndUsersCountFailed,
            });
        }
    }
</script>

<style lang="scss">
    .table-allocation-button-container {
        display: inline-block;

        position: absolute;
        right: 68px;
        padding-top: 8px;
        top: 0;
        height: 43px;
        pointer-events: all;
    }

    .view.open-bills .table-allocation-button-container {
        right: 20px;
    }
</style>
